// Lot 595 — niveau de batterie pendant un direct. App native → plugin Device.getBatteryInfo (iOS + Android, sondé
// toutes les 30 s) ; web / WebView Android → Battery Status API (événements levelchange / chargingchange) ;
// ancienne app sans le plugin → repli Battery API. Rien n'est bloquant : sans source, aucun rappel n'est émis.
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { hapticWarning } from "@/lib/haptics";

export const BATTERY_STEPS = [0.15, 0.10, 0.05];
const NATIVE_POLL_MS = 30000;

const normalize = (level, charging) => ({ level: typeof level === "number" && level >= 0 ? Math.min(1, level) : null, charging: !!charging });

// Appelle cb({ level: 0..1 | null, charging }) tout de suite puis à chaque changement ; retourne la fonction d'arrêt.
export function watchBattery(cb) {
  let stopped = false;
  let cleanup = () => {};
  const emit = (level, charging) => { if (!stopped) cb(normalize(level, charging)); };

  const webWatch = async () => {
    const getBattery = typeof navigator !== "undefined" && navigator.getBattery ? navigator.getBattery.bind(navigator) : null;
    if (!getBattery) return false;
    const b = await getBattery();
    if (stopped) return true;
    const onChange = () => emit(b.level, b.charging);
    b.addEventListener("levelchange", onChange);
    b.addEventListener("chargingchange", onChange);
    cleanup = () => { b.removeEventListener("levelchange", onChange); b.removeEventListener("chargingchange", onChange); };
    onChange();
    return true;
  };

  const nativeWatch = async () => {
    const { Device } = await import("@capacitor/device");
    const read = async () => { const info = await Device.getBatteryInfo(); emit(info.batteryLevel, info.isCharging); };
    await read();
    const timer = setInterval(() => read().catch(() => {}), NATIVE_POLL_MS);
    cleanup = () => clearInterval(timer);
  };

  (async () => {
    if (Capacitor.isNativePlatform()) {
      try { await nativeWatch(); return; } catch { /* plugin absent → repli web */ }
    }
    try { await webWatch(); } catch { /* aucune source : silence */ }
  })();

  return () => { stopped = true; cleanup(); };
}

// Paliers franchis (15 % → 10 % → 5 %) : `crossed` = tous les paliers ≥ niveau, `step` = le plus bas, à signaler s'il
// n'a pas déjà été montré. `charging` efface tout (on branche → plus d'alerte).
export const batteryAlertFor = (level, charging, alerted) => {
  if (charging || typeof level !== "number") return null;
  const crossed = BATTERY_STEPS.filter((s) => level <= s);
  if (!crossed.length) return null;
  const step = Math.min(...crossed);
  return alerted.includes(step) ? null : { step, crossed };
};

// Hôte en direct : { pct, step, show, dismiss } — bannière + vibration à chaque palier franchi (15 → 10 → 5 %), fermable,
// revient au palier suivant ; disparaît dès qu'on branche ; tout est remis à zéro quand le direct s'arrête.
export const useLiveBatteryAlert = (active) => {
  const [state, setState] = useState({ level: null, charging: false });
  const [alerted, setAlerted] = useState([]);
  const [dismissedStep, setDismissedStep] = useState(null);
  useEffect(() => {
    if (!active) { setAlerted([]); setDismissedStep(null); setState({ level: null, charging: false }); return undefined; }
    return watchBattery(setState);
  }, [active]);
  const due = active ? batteryAlertFor(state.level, state.charging, alerted) : null;
  useEffect(() => {
    if (!due) return;
    setAlerted((a) => Array.from(new Set([...a, ...due.crossed])));
    setDismissedStep(null);
    hapticWarning();
  }, [due?.step]); // eslint-disable-line react-hooks/exhaustive-deps
  const step = !active || state.charging || typeof state.level !== "number" ? null : (BATTERY_STEPS.filter((s) => state.level <= s).sort((a, b) => a - b)[0] ?? null);
  return { pct: typeof state.level === "number" ? Math.round(state.level * 100) : null, step, show: !!step && dismissedStep !== step, dismiss: () => setDismissedStep(step) };
};
